/**
 * Renders the captured DOM state in the Diagnostics panel's DOM tab.
 * Shows focused element, visible errors, and full button/input state
 * lists with RBAC-relevant flags (disabled, readonly, required, etc.).
 */
import { Box, Chip, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';

interface Props { domState: any }

export const DomStateView = ({ domState }: Props) => {
  const theme = useTheme();
  if (!domState) return null;

  return (
    <Box>
      <Box sx={{ fontFamily: 'monospace', mb: 1.5 }}>
        {domState.focusedElement && <Row label="Focused" value={domState.focusedElement} />}
        {domState.focusedValue && <Row label="Input value" value={domState.focusedValue} />}
        {typeof domState.openDialogs === 'number' && <Row label="Open dialogs" value={String(domState.openDialogs)} />}
        {domState.visibleErrors?.length > 0 && <Row label="Visible errors" value={domState.visibleErrors.join(' | ')} />}
        {typeof domState.scrollY === 'number' && <Row label="Scroll Y" value={`${domState.scrollY}px`} />}
        {domState.documentTitle && <Row label="Doc title" value={domState.documentTitle} />}
      </Box>

      {domState.buttons?.length > 0 && (
        <Box sx={{ mb: 1.5 }}>
          <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.72rem', display: 'block', mb: 0.5 }}>
            Buttons & Links ({domState.buttons.length})
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
            {domState.buttons.map((b: any, i: number) => (
              <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Chip size="small" label={b.tag} sx={{ height: 16, fontSize: '0.6rem', width: 46, flexShrink: 0 }} />
                <Chip size="small" label={b.disabled ? 'disabled' : 'enabled'} sx={{
                  height: 16, fontSize: '0.6rem', flexShrink: 0, width: 70, fontWeight: 600,
                  bgcolor: b.disabled ? alpha(theme.palette.warning.main, 0.15) : alpha(theme.palette.success.main, 0.15),
                  color: b.disabled ? theme.palette.warning.main : theme.palette.success.main,
                }} />
                <Typography variant="caption" sx={{ fontSize: '0.7rem', fontFamily: 'monospace', wordBreak: 'break-word' }}>
                  {b.label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {domState.inputs?.length > 0 && (
        <Box>
          <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.72rem', display: 'block', mb: 0.5 }}>
            Inputs & Fields ({domState.inputs.length})
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
            {domState.inputs.map((f: any, i: number) => (
              <Box key={i} sx={{ display: 'flex', gap: 0.75, alignItems: 'center', flexWrap: 'wrap' }}>
                <Chip size="small" label={f.type} sx={{ height: 16, fontSize: '0.6rem', flexShrink: 0, minWidth: 60 }} />
                <Typography variant="caption" sx={{ fontSize: '0.7rem', fontFamily: 'monospace', minWidth: 120, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {f.name}
                </Typography>
                {f.disabled && <FlagChip label="disabled" color={theme.palette.warning.main} />}
                {f.readOnly && <FlagChip label="readonly" color={theme.palette.info.main} />}
                {f.required && <FlagChip label="required" color={theme.palette.secondary.main} />}
                {f.invalid && <FlagChip label="invalid" color={theme.palette.error.main} />}
                {f.hasValue && <FlagChip label="has value" color={theme.palette.success.main} />}
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

const Row = ({ label, value }: { label: string; value: string }) => (
  <Box sx={{ display: 'flex', gap: 1, mb: 0.3 }}>
    <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.7rem', minWidth: 110, color: 'text.secondary' }}>{label}:</Typography>
    <Typography variant="caption" sx={{ fontSize: '0.7rem', fontFamily: 'monospace', wordBreak: 'break-all' }}>{value}</Typography>
  </Box>
);

const FlagChip = ({ label, color }: { label: string; color: string }) => (
  <Chip size="small" label={label} sx={{
    height: 16, fontSize: '0.6rem', flexShrink: 0,
    bgcolor: alpha(color, 0.15), color, fontWeight: 600,
  }} />
);
